import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Theme selection.
 *
 * Three states, not two: light, dark, and *system*. `system` stamps no
 * attribute at all, letting the stylesheet's media query decide, so the page
 * follows the operating system — including the accessibility-motivated
 * preferences — and keeps following it if it changes while open.
 */

export type Theme = 'light' | 'dark' | 'system';

/**
 * Light is the default rather than `system`.
 *
 * A service desk is a document-dense reading surface, and the light palette is
 * the one designed first here. Dark remains a first-class choice — and `system`
 * is still offered for people who genuinely want to follow the OS — but neither
 * is what a first-time visitor is handed. `index.html` carries it too, as
 * `data-theme` on the root element, for the frame a browser may paint before
 * this module has run.
 */
export const DEFAULT_THEME: Theme = 'light';

const STORAGE_KEY = 'aurora.theme';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** What is actually rendering right now, with `system` resolved. */
  resolved: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    // Private browsing, or site data blocked. Neither is an error worth
    // surfacing — the theme simply will not persist.
  }
  return DEFAULT_THEME;
}

/**
 * Puts a theme on the document.
 *
 * `system` removes the attribute entirely rather than setting a resolved
 * value, so the stylesheet's media query stays authoritative.
 */
function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
}

/**
 * Applied the moment this module loads — before React renders anything.
 *
 * The provider used to set the attribute in an effect, which runs after the
 * first paint, so an OS in dark mode flashed a dark frame on every load before
 * the default light theme arrived (and the reverse for a stored Dark). An
 * inline script in index.html would be earlier still, but the content policy
 * allows scripts from this origin only, and `main.tsx` imports this module
 * before it mounts anything.
 */
applyTheme(readStoredTheme());

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);
  const [systemDark, setSystemDark] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-color-scheme: dark)').matches,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  // A layout effect, so a change of theme is on the page before it repaints.
  useLayoutEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persisting is a convenience; failing to do so must not break the toggle.
    }
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      setTheme,
      resolved: theme === 'system' ? (systemDark ? 'dark' : 'light') : theme,
    }),
    [theme, setTheme, systemDark],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside a ThemeProvider');
  return context;
}
